import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, PlayCircle, ExternalLink, FlaskConical, Network } from "lucide-react";
import { Badge } from "./Primitives";
import { formatNumber, cn } from "../lib/utils";
import { useRole } from "../lib/role.jsx";

// Lightweight "Simulate" drawer that previews the Peso impact of a
// recommendation and deep-links into the Simulation Workbench or the
// Operational Digital Twin (based on the user's RBAC).

const SCENARIO_FOR_ENGINE = {
  demand_forecast:   { id: "summer-surge",      label: "Summer Demand Surge" },
  stockout_adjusted: { id: "summer-surge",      label: "Summer Demand Surge" },
  inventory_health:  { id: "sku-rationalize",   label: "SKU Rationalisation" },
  stockout_risk:     { id: "supply-disruption", label: "Supply-Chain Disruption" },
  replenishment:     { id: "po-lead-time",      label: "PO Lead-Time Shift" },
  redistribution:    { id: "moq-optimization",  label: "MOQ / Network Rebalance" },
  aging:             { id: "sku-rationalize",   label: "SKU Rationalisation" },
};

export default function SimulateDrawer({ open, rec, onClose }) {
  const navigate = useNavigate();
  const { canAccess } = useRole();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !rec) return null;
  const scenario = SCENARIO_FOR_ENGINE[rec.engine] || { id: "summer-surge", label: "Summer Demand Surge" };
  const hasWorkbench = canAccess("/workbench");
  const hasTwin = canAccess("/twin");

  function openInWorkbench() {
    sessionStorage.setItem("workbench-prefill", JSON.stringify({
      scenario: scenario.id, source: "morning-brief", rec_id: rec.id,
      sku_id: rec.scope?.sku_id, branch_id: rec.scope?.branch_id,
    }));
    navigate("/workbench");
    onClose();
  }
  function openInTwin() {
    sessionStorage.setItem("twin-prefill", JSON.stringify({
      scenario: scenario.id, source: "morning-brief", rec_id: rec.id,
    }));
    navigate("/twin");
    onClose();
  }

  return createPortal(
    <>
      <div data-testid="simulate-drawer-backdrop" onClick={onClose}
           className="fixed inset-0 bg-black/55 z-40 animate-fade-in" />
      <aside data-testid="simulate-drawer"
             className={cn("fixed top-0 right-0 h-screen w-full sm:w-[520px] bg-surface border-l border-border z-50",
               "shadow-2xl overflow-y-auto animate-slide-in-right")}>
        <div className="px-5 py-4 border-b border-border flex items-center gap-3 sticky top-0 bg-surface z-10">
          <div className="w-9 h-9 bg-info/10 border border-info/30 rounded-sm flex items-center justify-center text-info">
            <PlayCircle size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">Simulate · {rec.engine_label}</div>
            <div className="text-sm font-medium truncate">{rec.title}</div>
          </div>
          <button data-testid="simulate-drawer-close" onClick={onClose}
                  className="w-7 h-7 hover:bg-surface-2 rounded-sm flex items-center justify-center">
            <X size={15} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Expected PHP" value={`₱${formatNumber(rec.expected_value_php || 0, { compact: true })}`}
                  testid="sim-expected" />
            <Stat label="Confidence" value={rec.confidence?.tier || "medium"}
                  sub={rec.confidence?.prob != null ? `${Math.round(rec.confidence.prob * 100)}%` : null}
                  testid="sim-confidence" />
          </div>

          <section>
            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1.5">Pre-selected scenario</div>
            <div className="border border-border rounded-sm p-3 bg-surface-2/40">
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical size={13} className="text-primary" />
                <span className="font-medium text-[13px]">{scenario.label}</span>
                <Badge status="info" className="ml-auto">{scenario.id}</Badge>
              </div>
              <div className="text-[11.5px] text-muted leading-relaxed">
                We'll pre-fill the Workbench with the SKU, branch, and action class from this recommendation.
                You can override every parameter before you run the simulation.
              </div>
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1.5">Scope</div>
            <div className="grid grid-cols-2 gap-2 text-[11.5px]">
              <ScopeRow k="SKU" v={rec.scope?.sku_name || rec.scope?.sku_id || "—"} />
              <ScopeRow k="Branch" v={rec.scope?.branch_name || rec.scope?.branch_id || rec.scope?.receiver_branch || "network"} />
              <ScopeRow k="ABC × XYZ" v={rec.scope?.abc_xyz || "—"} />
              <ScopeRow k="Action class" v={rec.action_class || "—"} />
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1.5">AI recommends</div>
            <div className="border-l-2 border-primary pl-3 text-[12.5px] text-fg/95 leading-relaxed">
              {rec.ai_recommends}
            </div>
          </section>

          <div className="flex items-center gap-2 pt-2 border-t border-border">
            {hasWorkbench && (
              <button data-testid="sim-open-workbench" onClick={openInWorkbench}
                      className="flex-1 px-3 py-2 text-[12px] bg-primary text-primary-fg hover:opacity-90 rounded-sm font-semibold inline-flex items-center justify-center gap-1.5">
                <FlaskConical size={13} /> Open in Workbench
                <ExternalLink size={10} />
              </button>
            )}
            {hasTwin && (
              <button data-testid="sim-open-twin" onClick={openInTwin}
                      className={cn("px-3 py-2 text-[12px] border border-info text-info hover:bg-info hover:text-primary-fg rounded-sm font-semibold inline-flex items-center justify-center gap-1.5",
                        hasWorkbench ? "" : "flex-1")}>
                <Network size={13} /> Open in Twin
                <ExternalLink size={10} />
              </button>
            )}
            {!hasWorkbench && !hasTwin && (
              <div className="text-[11.5px] text-muted italic">
                Simulation modules are paused for the pilot phase or outside your access profile. Approve the action directly on the card.
              </div>
            )}
          </div>
        </div>
      </aside>
    </>,
    document.body,
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="border border-border rounded-sm bg-surface-2/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div className="font-mono-tabular text-lg font-semibold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function ScopeRow({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border/60 pb-1">
      <span className="text-muted uppercase tracking-widest text-[10px]">{k}</span>
      <span className="font-medium text-right truncate max-w-[220px]">{v}</span>
    </div>
  );
}
